import { useEffect, useRef, useState } from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { CHART_VARS } from './chartVars';
import { DataTable } from './DataTable';

export type BarSeries = { name: string; values: number[] };

type Props = {
  categories: string[];
  /** 1 to 3 series. Colours are fixed by position: series 1 blue, 2 orange, 3 aqua. */
  series: BarSeries[];
  /** how a value is written in the tooltip and the table, e.g. jod */
  format: (n: number) => string;
  title: string;
  height?: number;
};

/** A tidy y-axis top: 60,000 for a biggest value of 57,310 */
function niceMax(max: number) {
  if (max <= 0) return 1;
  const step = 10 ** Math.floor(Math.log10(max));
  return Math.ceil(max / step) * step;
}
const short = (n: number) => (n >= 1000 ? `${n / 1000}k` : String(n));

/** Grouped bars with a legend, a tooltip on every column, and a table view for anyone who prefers numbers */
export function BarChart({ categories, series, format, title, height = 220 }: Props) {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  // draw at the width we really have, so the text stays readable
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(480);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const watch = new ResizeObserver(() => setW(Math.max(280, el.clientWidth)));
    watch.observe(el);
    setW(Math.max(280, el.clientWidth));
    return () => watch.disconnect();
  }, [view]);
  const left = 40;
  const bottom = 24;
  const plotH = height - bottom - 8;
  const top = niceMax(Math.max(0, ...series.flatMap((s) => s.values)));
  const slot = (W - left) / Math.max(categories.length, 1);
  const bar = Math.min(22, (slot - 8) / series.length - 2);
  const y = (v: number) => 8 + plotH - (v / top) * plotH;

  return (
    <Box ref={box} sx={{ ...CHART_VARS, minWidth: 0 }}>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 1 }}
      >
        <Stack direction="row" spacing={2}>
          {series.map((s, i) => (
            <Stack key={s.name} direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: `var(--series-${i + 1})` }} />
              <Typography variant="caption">{s.name}</Typography>
            </Stack>
          ))}
        </Stack>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={view}
          onChange={(_e, v: 'chart' | 'table' | null) => v && setView(v)}
          aria-label={`${title}: chart or table`}
        >
          <ToggleButton value="chart" sx={{ textTransform: 'none', px: 1.25, py: 0.25 }}>
            Chart
          </ToggleButton>
          <ToggleButton value="table" sx={{ textTransform: 'none', px: 1.25, py: 0.25 }}>
            Table
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {view === 'chart' ? (
        <svg
          viewBox={`0 0 ${W} ${height}`}
          width="100%"
          role="img"
          aria-label={`${title}: ${series.map((s) => s.name).join(', ')} by ${categories.length} periods`}
          style={{ display: 'block' }}
        >
          {[0, 0.25, 0.5, 0.75, 1].map((f) => (
            <g key={f}>
              <line
                x1={left}
                x2={W}
                y1={y(top * f)}
                y2={y(top * f)}
                stroke="var(--mui-palette-divider)"
                strokeWidth="1"
              />
              <text
                x={left - 6}
                y={y(top * f) + 4}
                textAnchor="end"
                fontSize="11"
                fill="var(--mui-palette-text-secondary)"
              >
                {short(top * f)}
              </text>
            </g>
          ))}
          {categories.map((c, ci) => {
            const x0 = left + ci * slot + (slot - (bar + 2) * series.length) / 2;
            return (
              <Tooltip
                key={c}
                title={
                  <Box>
                    <b>{c}</b>
                    {series.map((s) => (
                      <div key={s.name}>
                        {s.name}: {format(s.values[ci] ?? 0)}
                      </div>
                    ))}
                  </Box>
                }
                followCursor
              >
                <g>
                  <rect x={left + ci * slot} y={0} width={slot} height={height - bottom} fill="transparent" />
                  {series.map((s, si) => {
                    const v = s.values[ci] ?? 0;
                    const h = Math.max(plotH - (y(v) - 8), 0);
                    return (
                      <rect
                        key={s.name}
                        x={x0 + si * (bar + 2)}
                        y={y(v)}
                        width={bar}
                        height={h}
                        rx={3}
                        fill={`var(--series-${si + 1})`}
                      />
                    );
                  })}
                  <text
                    x={left + ci * slot + slot / 2}
                    y={height - 6}
                    textAnchor="middle"
                    fontSize="11"
                    fill="var(--mui-palette-text-secondary)"
                  >
                    {c}
                  </text>
                </g>
              </Tooltip>
            );
          })}
        </svg>
      ) : (
        <DataTable<{ c: string; i: number }>
          rows={categories.map((c, i) => ({ c, i }))}
          rowKey={(r) => r.c}
          columns={[
            { header: 'Period', primary: true, render: (r) => r.c },
            ...series.map((s) => ({
              header: s.name,
              nowrap: true,
              render: (r: { i: number }) => format(s.values[r.i] ?? 0),
            })),
          ]}
        />
      )}
    </Box>
  );
}
